import { useState } from 'react';
import axiosInstance from '../api/axiosConfig';
import { useToast } from '../context/ToastContext';
import jsPDF from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import type { Member, FinancialRecord } from '../models';

interface Header {
    label: string;
    key: string;
}

// Utility function for CSV export (can be moved to a separate utils file if common)
const exportToCsv = (headers: Header[], data: any[], filename: string, showToast: (message: string, type: 'success' | 'error' | 'info') => void) => {
    if (!data || data.length === 0) {
        showToast('No data to export.', 'info');
        return;
    }

    const csvRows = [];
    const headerRow = headers.map(header => header.label).join(',');
    csvRows.push(headerRow);

    for (const row of data) {
        const values = headers.map(header => {
            const value = row[header.key];
            return `"${String(value).replace(/"/g, '""')}"`;
        }).join(',');
        csvRows.push(values);
    }

    const csvString = csvRows.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `${filename}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
};

// Utility function for PDF export (can be moved to a separate utils file if common)
const exportToPdf = (headers: Header[], data: any[], filename: string, title: string, showToast: (message: string, type: 'success' | 'error' | 'info') => void) => {
    if (!data || data.length === 0) {
        showToast('No data to export.', 'info');
        return;
    }

    const doc = new jsPDF();
    const tableHeaders = headers.map(header => header.label);
    const tableBody = data.map(row =>
        headers.map(header => (row as any)[header.key])
    );

    autoTable(doc, {
        head: [tableHeaders],
        body: tableBody,
        startY: 20,
        theme: 'striped',
        headStyles: { fillColor: [22, 160, 133] },
        didDrawPage: function (data: any) {
            doc.setFontSize(16);
            doc.setTextColor(40);
            doc.text(title, data.settings.margin.left, 10);

            let str = 'Page ' + (doc as any).internal.getNumberOfPages();
            doc.setFontSize(10);
            doc.text(str, doc.internal.pageSize.width - data.settings.margin.right, doc.internal.pageSize.height - 10, { align: 'right' });
        }
    });

    doc.save(`${filename}.pdf`);
};


export const useAccountReportGenerator = () => {
    // Removed: loadingAccountMembers and accountMembersReportFormat states
    const [loadingFinancialRecords, setLoadingFinancialRecords] = useState(false);


    const { showToast } = useToast();

    // Removed: handleGenerateAccountMembersReport function

    const handleGenerateAccountFinancialReport = async (financialReportFormat:string, selectedAccountId: string, accountName: string) => {
        if (!selectedAccountId) {
            showToast('Please select an account first.', 'warning');
            return;
        }

        setLoadingFinancialRecords(true);
        try {
            const response = await axiosInstance.get<FinancialRecord[]>(`/reports/financialRecordsByAccount/${selectedAccountId}`);
            const records = response.data;

            if (records.length === 0) {
                showToast(`No financial records found for the selected account (${accountName}) made by admins.`, 'info');
                return;
            }

            const headers: Header[] = [
                { label: 'Record ID', key: '_id' },


                { label: 'Category', key: 'category' },
                { label: 'Description', key: 'description' },
                { label: 'Amount', key: 'amount' },
                { label: 'Recorded By', key: 'recordedBy' },
                { label: 'Recorded At', key: 'createdAt' },
            ];

            const formattedData = records.map((record:any) => ({
                ...record,
                date: record.date ? new Date(record.date).toLocaleDateString() : '',
                createdAt: record.createdAt ? new Date(record.createdAt).toLocaleString() : '',
                recordedBy: record.recordedBy ? `${record.recordedBy.firstName} ${record.recordedBy.lastName}` : 'N/A',
            }));

            const filename = `${accountName.replace(/[^a-zA-Z0-9]/g, '_')}_financial_report_by_admins`;

            if (financialReportFormat === 'csv') {
                //exportToCsv(headers, formattedData, filename, showToast);
                showToast(`Financial records report for ${accountName} (CSV) generated successfully!`, 'success');
            } else if (financialReportFormat === 'pdf') {
                exportToPdf(headers, formattedData, filename, `Financial Report for ${accountName}`, showToast);
                showToast(`Financial records report for ${accountName} (PDF) generated successfully!`, 'success');
            }

        } catch (error) {
            console.error('Failed to generate account financial report:', error);
            showToast('Failed to generate account financial report. Please try again.', 'error');
        } finally {
            setLoadingFinancialRecords(false);
        }
    };

    return {
setLoadingFinancialRecords,
        loadingFinancialRecords,


        handleGenerateAccountFinancialReport,
    };
};

